/**
 * Privacy Policy Page
 *
 * v2.60 - 2026-03-24 - Refactored to use LegalPageShell (sticky bar, TOC,
 *                       scroll-spy, print styles, dark-mode safe colours)
 *                       Removed Effective Date / Version line from hero.
 *                       hello@ → legal@mylojic.com in contact section.
 *                       secondary.main body text → legalBodyTextSx (dark-mode safe).
 */
import React from 'react';
import {
  Box,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import LegalPageShell, { legalBodyTextSx, LEGAL_STICKY_BAR_HEIGHT } from '../components/LegalPageShell';

const SECTIONS = [
  { id: 'intro',         label: '1. Introduction' },
  { id: 'collect',      label: '2. Information We Collect' },
  { id: 'use',          label: '3. How We Use Your Information' },
  { id: 'storage',      label: '4. Data Storage and Security' },
  { id: 'sharing',      label: '5. Data Sharing and Disclosure' },
  { id: 'rights',       label: '6. Your Rights and Choices' },
  { id: 'availability', label: '7. Service Availability' },
  { id: 'california',   label: '8. California Residents (CCPA/CPRA)' },
  { id: 'retention',    label: '9. Data Retention' },
  { id: 'children',     label: '10. Children\'s Privacy' },
  { id: 'international',label: '11. International Data Transfers' },
  { id: 'thirdparty',   label: '12. Third-Party Links' },
  { id: 'changes',      label: '13. Changes to This Policy' },
  { id: 'contact',      label: '14. Contact Us' },
];

const sectionHeadingSx = {
  color: 'primary.main',
  fontWeight: 700,
  mb: 3,
  scrollMarginTop: `${LEGAL_STICKY_BAR_HEIGHT + 24}px`,
};

const subHeadingSx = {
  color: 'primary.main',
  fontWeight: 600,
  mb: 2,
};

const Privacy: React.FC = () => {
  return (
    <LegalPageShell
      title="Privacy Policy"
      lastUpdated="March 23, 2026"
      sections={SECTIONS}
    >
      {/* Section 1 */}
      <Typography id="intro" variant="h4" sx={sectionHeadingSx}>
        1. Introduction
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        Data Edge Services LLC, a Texas limited liability company doing business as MyLojic ("Company," "we," "us," or "our"), is committed to protecting the privacy and security of your personal information. This Privacy Policy describes how we collect, use, store, share, and protect information when you use the MyLojic platform, including our website at www.mylojic.com, our web application, mobile applications, APIs, and all related services (collectively, the "Platform").
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        This Privacy Policy applies to all users of the Platform, including account holders, authorized users, and visitors to our website. By accessing or using the Platform, you acknowledge that you have read and understood this Privacy Policy.
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        We encourage you to read this Privacy Policy carefully and contact us if you have any questions.
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* Section 2 */}
      <Typography id="collect" variant="h4" sx={sectionHeadingSx}>
        2. Information We Collect
      </Typography>

      <Typography variant="h5" sx={subHeadingSx}>
        2.1 Information You Provide Directly
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        We collect information that you provide to us directly, including:
      </Typography>
      <Box component="ul" sx={{ ...legalBodyTextSx, mb: 3 }}>
        <li>Account registration information: name, email address, company name, job title, phone number</li>
        <li>Billing and payment information: billing address, payment method details (processed securely by our third-party payment processor)</li>
        <li>Profile information: preferences, communication settings, and account configurations</li>
        <li>Communications: messages you send to us through support channels, feedback forms, or email</li>
        <li>Survey responses and feedback you choose to provide</li>
      </Box>

      <Typography variant="h5" sx={subHeadingSx}>
        2.2 Business Data You Upload
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        When you use the Platform, you may upload or connect business data from various sources. This data may include:
      </Typography>
      <Box component="ul" sx={{ ...legalBodyTextSx, mb: 3 }}>
        <li>Financial records (invoices, bills, payments, accounts receivable, accounts payable)</li>
        <li>Customer and vendor information (names, contact details, transaction history)</li>
        <li>Inventory and product data</li>
        <li>Employee and payroll information (if connected through HR integrations)</li>
        <li>Sales and revenue data</li>
        <li>Any other business data imported through Third-Party Integrations such as QuickBooks, Shopify, Gusto, Xero, and similar services</li>
      </Box>
      <Paper elevation={1} sx={{ p: 2, bgcolor: 'warning.light', mb: 3 }}>
        <Typography sx={{ color: 'warning.dark', fontWeight: 600 }}>
          Important: Your Business Data may contain personally identifiable information (PII) about your customers, employees, or vendors. You are responsible for ensuring you have the appropriate rights and consents to share this data with the Platform. Our AI-powered data discovery system automatically identifies and classifies PII within your Business Data to help you maintain compliance.
        </Typography>
      </Paper>

      <Typography variant="h5" sx={subHeadingSx}>
        2.3 Information Collected Automatically
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        When you access or use the Platform, we automatically collect certain information, including:
      </Typography>
      <Box component="ul" sx={{ ...legalBodyTextSx, mb: 3 }}>
        <li>Device information: browser type, operating system, device identifiers, screen resolution</li>
        <li>Usage data: pages visited, features used, actions taken, timestamps, session duration</li>
        <li>Log data: IP address, access times, error logs, referring URLs</li>
        <li>Performance data: page load times, system errors, and diagnostic information</li>
        <li>Authentication data: login timestamps, multi-factor authentication events, session tokens</li>
      </Box>

      <Typography variant="h5" sx={subHeadingSx}>
        2.4 Information from Third-Party Integrations
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        When you connect Third-Party Integrations (such as QuickBooks, Shopify, or Gusto), we receive data from those services based on the permissions you grant. The types of data vary by integration and may include financial records, customer data, inventory information, and other business data. We only access data necessary to provide our services.
      </Typography>

      <Typography variant="h5" sx={subHeadingSx}>
        2.5 Cookies and Tracking Technologies
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        We use cookies and similar tracking technologies to enhance your experience:
      </Typography>
      <Box component="ul" sx={{ ...legalBodyTextSx, mb: 3 }}>
        <li>Essential cookies: Required for Platform functionality, authentication, and security</li>
        <li>Analytics cookies: Help us understand how you use the Platform to improve our services</li>
        <li>Preference cookies: Remember your settings and preferences</li>
      </Box>
      <Typography paragraph sx={legalBodyTextSx}>
        We do not use advertising or third-party tracking cookies. On your first visit, a cookie consent banner allows you to accept all cookies or limit cookies to essential-only. Essential cookies cannot be disabled as they are required for authentication and security. You may change your preference at any time by clearing your browser's localStorage.
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* Section 3 */}
      <Typography id="use" variant="h4" sx={sectionHeadingSx}>
        3. How We Use Your Information
      </Typography>

      <Typography variant="h5" sx={subHeadingSx}>
        3.1 Providing and Operating the Platform
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        We use your information to:
      </Typography>
      <Box component="ul" sx={{ ...legalBodyTextSx, mb: 3 }}>
        <li>Create and manage your account</li>
        <li>Provide, maintain, and improve the Platform and its features</li>
        <li>Process and analyze your Business Data through our data pipeline</li>
        <li>Generate AI-powered insights, analytics, dashboards, and recommendations</li>
        <li>Propose data transformations and categorizations for your approval</li>
        <li>Process payments and manage your subscription</li>
      </Box>

      <Typography variant="h5" sx={subHeadingSx}>
        3.2 Communication
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        We use your contact information to:
      </Typography>
      <Box component="ul" sx={{ ...legalBodyTextSx, mb: 3 }}>
        <li>Send service-related notices, updates, and security alerts</li>
        <li>Respond to your inquiries and support requests</li>
        <li>Send product updates and feature announcements (with your consent)</li>
        <li>Notify you of data transformation proposals awaiting your approval</li>
      </Box>

      <Typography variant="h5" sx={subHeadingSx}>
        3.3 Security and Compliance
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        We use information to:
      </Typography>
      <Box component="ul" sx={{ ...legalBodyTextSx, mb: 3 }}>
        <li>Detect, prevent, and respond to security threats, fraud, and abuse</li>
        <li>Enforce our Terms of Service and End User License Agreement</li>
        <li>Comply with legal obligations and regulatory requirements</li>
        <li>Maintain audit trails for compliance purposes (retained for 7 years)</li>
        <li>Perform tenant isolation verification and access control</li>
      </Box>

      <Typography variant="h5" sx={subHeadingSx}>
        3.4 Analytics and Improvement
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        We use aggregated and anonymized data to:
      </Typography>
      <Box component="ul" sx={{ ...legalBodyTextSx, mb: 3 }}>
        <li>Analyze usage patterns and trends to improve the Platform</li>
        <li>Develop new features and services</li>
        <li>Improve our AI models and data processing algorithms</li>
        <li>Generate aggregate benchmarks and industry insights (never containing identifiable business data)</li>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 4 */}
      <Typography id="storage" variant="h4" sx={sectionHeadingSx}>
        4. Data Storage and Security
      </Typography>

      <Typography variant="h5" sx={subHeadingSx}>
        4.1 Infrastructure
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        Your data is stored on Microsoft Azure cloud infrastructure, primarily in the East US 2 region with disaster recovery capabilities in the Central US region. Our infrastructure includes:
      </Typography>
      <Box component="ul" sx={{ ...legalBodyTextSx, mb: 3 }}>
        <li>Azure PostgreSQL for structured data with automatic encryption at rest</li>
        <li>Azure Blob Storage for file storage with AES-256 encryption</li>
        <li>Azure Key Vault for secrets management and OAuth token storage</li>
        <li>Azure Container Apps for service hosting with private networking</li>
        <li>Azure Front Door and Cloudflare for DDoS protection and traffic routing</li>
      </Box>

      <Typography variant="h5" sx={subHeadingSx}>
        4.2 Security Measures
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        We implement comprehensive security measures to protect your data:
      </Typography>
      <Box component="ul" sx={{ ...legalBodyTextSx, mb: 3 }}>
        <li>Encryption at rest using AES-256 encryption for all stored data</li>
        <li>Encryption in transit using TLS 1.2 or higher for all data transmissions</li>
        <li>Logical multi-tenant isolation ensuring your data is separated from other customers via attribute-based access controls</li>
        <li>Row-Level Security (RLS) in analytics ensuring each tenant can only access their own specific data subset</li>
        <li>Managed identity authentication eliminating the need for credentials in application code</li>
        <li>Network isolation using virtual networks, private endpoints, and firewall rules</li>
        <li>Multi-factor authentication for user accounts</li>
        <li>Regular security audits and vulnerability assessments</li>
        <li>7-year audit trail for all data access and modifications</li>
      </Box>

      <Typography variant="h5" sx={subHeadingSx}>
        4.3 Compliance Standards
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        Our security infrastructure is designed to meet the requirements of:
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Standard</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow><TableCell>SOC 2 Type II</TableCell><TableCell>Service Organization Controls for security, availability, and confidentiality</TableCell></TableRow>
            <TableRow><TableCell>GDPR</TableCell><TableCell>General Data Protection Regulation compliance for EU data subjects</TableCell></TableRow>
            <TableRow><TableCell>CCPA / CPRA</TableCell><TableCell>California Consumer Privacy Act and California Privacy Rights Act compliance</TableCell></TableRow>
            <TableRow><TableCell>HIPAA</TableCell><TableCell>Health Insurance Portability and Accountability Act readiness (when handling health data)</TableCell></TableRow>
            <TableRow><TableCell>ISO 27001</TableCell><TableCell>Information security management system standards</TableCell></TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 4 }} />

      {/* Section 5 */}
      <Typography id="sharing" variant="h4" sx={sectionHeadingSx}>
        5. Data Sharing and Disclosure
      </Typography>

      <Typography variant="h5" sx={subHeadingSx}>
        5.1 We Do Not Sell Your Data
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        We do not sell, rent, or trade your personal information or Business Data to third parties for their marketing or advertising purposes.
      </Typography>

      <Typography variant="h5" sx={subHeadingSx}>
        5.2 Service Providers
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        We share data with trusted service providers who assist us in operating the Platform. These providers are contractually obligated to protect your data and may only use it to perform services on our behalf:
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Provider</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Purpose</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Data Shared</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow><TableCell>Microsoft Azure</TableCell><TableCell>Cloud infrastructure, database, storage, compute</TableCell><TableCell>All Platform data (encrypted at rest)</TableCell></TableRow>
            <TableRow><TableCell>Microsoft Azure OpenAI</TableCell><TableCell>AI-powered analytics and insights</TableCell><TableCell>Business Data for analysis (not stored for AI training by Microsoft)</TableCell></TableRow>
            <TableRow><TableCell>Clerk</TableCell><TableCell>User authentication and session management</TableCell><TableCell>Email, name, authentication events</TableCell></TableRow>
            <TableRow><TableCell>SendGrid (Twilio)</TableCell><TableCell>Transactional email delivery</TableCell><TableCell>Name, email address</TableCell></TableRow>
            <TableRow><TableCell>Stripe</TableCell><TableCell>Payment processing and subscription management</TableCell><TableCell>Billing information (credit card handled by Stripe, not stored by us)</TableCell></TableRow>
            <TableRow><TableCell>Cloudflare</TableCell><TableCell>DNS, CDN, and DDoS protection</TableCell><TableCell>IP address, request metadata (Cloudflare privacy policy applies)</TableCell></TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h5" sx={subHeadingSx}>
        5.3 Legal Requirements
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        We may disclose your information if required to do so by law, regulation, legal process, or governmental request, or when we believe disclosure is necessary to protect our rights, your safety, or the safety of others, investigate fraud, or respond to a government request.
      </Typography>

      <Typography variant="h5" sx={subHeadingSx}>
        5.4 Business Transfers
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        In the event of a merger, acquisition, reorganization, bankruptcy, or sale of all or a portion of our assets, your information may be transferred as part of that transaction. We will notify you of any such change and any choices you may have regarding your information.
      </Typography>

      <Typography variant="h5" sx={subHeadingSx}>
        5.5 AI Data Processing
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        When we process your Business Data using Azure OpenAI services, your data is processed in accordance with Microsoft's data processing terms. Your data is not used by Microsoft to train or improve their AI models. All AI processing occurs within our Azure tenant with enterprise-grade security controls.
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* Section 6 */}
      <Typography id="rights" variant="h4" sx={sectionHeadingSx}>
        6. Your Rights and Choices
      </Typography>

      <Typography variant="h5" sx={subHeadingSx}>6.1 Access and Portability</Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        You have the right to access the personal information we hold about you and to receive a copy of your data in a commonly used, machine-readable format. You can export your Business Data at any time through the Platform's export functionality.
      </Typography>

      <Typography variant="h5" sx={subHeadingSx}>6.2 Correction</Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        You have the right to request that we correct any inaccurate or incomplete personal information. You can update most of your account information directly through the Platform.
      </Typography>

      <Typography variant="h5" sx={subHeadingSx}>6.3 Deletion</Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        You have the right to request deletion of your personal information and Business Data. Upon receiving a verified deletion request, we will delete your data within thirty (30) days, except where retention is required by law. To request deletion, contact us at legal@mylojic.com or through your account settings.
      </Typography>

      <Typography variant="h5" sx={subHeadingSx}>6.4 Restriction of Processing</Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        You have the right to request that we restrict the processing of your personal information under certain circumstances, such as when you contest the accuracy of the data or object to our processing.
      </Typography>

      <Typography variant="h5" sx={subHeadingSx}>6.5 Objection to Processing</Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        You have the right to object to the processing of your personal information where we rely on legitimate interests as our legal basis.
      </Typography>

      <Typography variant="h5" sx={subHeadingSx}>6.6 Withdrawal of Consent</Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        Where we process your information based on your consent, you have the right to withdraw that consent at any time. Withdrawal of consent does not affect the lawfulness of processing prior to withdrawal.
      </Typography>

      <Typography variant="h5" sx={subHeadingSx}>6.7 Non-Discrimination</Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        We will not discriminate against you for exercising any of your privacy rights. You will not receive different pricing, quality of service, or level of access for exercising your rights.
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* Section 7 */}
      <Typography id="availability" variant="h4" sx={sectionHeadingSx}>
        7. Service Availability
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        The MyLojic platform is currently available to businesses located in the <strong>United States only</strong>. We do not actively market or provide the Service to residents of the European Union, European Economic Area, United Kingdom, or other international jurisdictions at this time.
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        If you access the Platform from outside the United States, you do so at your own initiative and are responsible for compliance with local laws. Your data is stored and processed in the United States (Microsoft Azure East US 2 region).
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        <strong>Future European Expansion:</strong> We intend to expand the Service to European markets in the future. When that occurs, we will extend full compliance with the General Data Protection Regulation (GDPR) and appoint a Data Protection Officer. We will notify existing users and update this Privacy Policy before any European service launch.
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* Section 8 */}
      <Typography id="california" variant="h4" sx={sectionHeadingSx}>
        8. Additional Rights for California Residents (CCPA/CPRA)
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        If you are a California resident, you have the following rights under the California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA):
      </Typography>
      <Box component="ul" sx={{ ...legalBodyTextSx, mb: 3 }}>
        <li>Right to know what personal information we collect, use, disclose, and sell</li>
        <li>Right to delete personal information we hold about you</li>
        <li>Right to opt-out of the sale or sharing of personal information (we do not sell your data)</li>
        <li>Right to correct inaccurate personal information</li>
        <li>Right to limit the use of sensitive personal information</li>
        <li>Right to non-discrimination for exercising your privacy rights</li>
      </Box>
      <Typography paragraph sx={legalBodyTextSx}>
        To exercise your CCPA/CPRA rights, contact us at legal@mylojic.com.
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* Section 9 */}
      <Typography id="retention" variant="h4" sx={sectionHeadingSx}>
        9. Data Retention
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        We retain your information for as long as necessary to provide the Platform and fulfill the purposes described in this Privacy Policy:
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Data Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Retention Period</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow><TableCell>Account information</TableCell><TableCell>Duration of account + 30 days</TableCell><TableCell>Service provision</TableCell></TableRow>
            <TableRow><TableCell>Business Data</TableCell><TableCell>Duration of account + 30 days</TableCell><TableCell>Service provision; deleted upon request</TableCell></TableRow>
            <TableRow><TableCell>Audit logs</TableCell><TableCell>7 years</TableCell><TableCell>Compliance and security</TableCell></TableRow>
            <TableRow><TableCell>Billing records</TableCell><TableCell>7 years</TableCell><TableCell>Tax and legal requirements</TableCell></TableRow>
            <TableRow><TableCell>Usage analytics</TableCell><TableCell>2 years (anonymized)</TableCell><TableCell>Product improvement</TableCell></TableRow>
            <TableRow><TableCell>Support communications</TableCell><TableCell>3 years</TableCell><TableCell>Service quality</TableCell></TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 4 }} />

      {/* Section 10 */}
      <Typography id="children" variant="h4" sx={sectionHeadingSx}>
        10. Children's Privacy
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        The Platform is designed for business use and is not intended for children under the age of 16. We do not knowingly collect personal information from children under 16. If we become aware that we have collected personal information from a child under 16, we will take steps to delete that information promptly. If you believe a child has provided us with personal information, please contact us at legal@mylojic.com.
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* Section 11 */}
      <Typography id="international" variant="h4" sx={sectionHeadingSx}>
        11. International Data Transfers
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        Your data is primarily stored and processed in the United States (Microsoft Azure East US 2 region). If you access the Platform from outside the United States, you understand and consent to the transfer of your data to the United States.
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        For data transfers from the EU/EEA, we rely on Standard Contractual Clauses approved by the European Commission and other appropriate safeguards to ensure your data is protected in accordance with GDPR requirements.
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* Section 12 */}
      <Typography id="thirdparty" variant="h4" sx={sectionHeadingSx}>
        12. Third-Party Links and Services
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        The Platform may contain links to third-party websites or services that are not owned or controlled by the Company. We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party services you access through the Platform.
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* Section 13 */}
      <Typography id="changes" variant="h4" sx={sectionHeadingSx}>
        13. Changes to This Privacy Policy
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make material changes, we will:
      </Typography>
      <Box component="ul" sx={{ ...legalBodyTextSx, mb: 3 }}>
        <li>Post the updated Privacy Policy on the Platform with a new effective date</li>
        <li>Notify you via email or through the Platform at least thirty (30) days before the changes take effect</li>
        <li>Provide a summary of the key changes</li>
      </Box>
      <Typography paragraph sx={legalBodyTextSx}>
        Your continued use of the Platform after the effective date of any changes constitutes your acceptance of the updated Privacy Policy. If you do not agree with the changes, you should stop using the Platform and contact us about deleting your account.
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* Section 14 */}
      <Typography id="contact" variant="h4" sx={sectionHeadingSx}>
        14. Contact Us
      </Typography>
      <Typography paragraph sx={legalBodyTextSx}>
        If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
      </Typography>
      <Paper elevation={1} sx={{ p: 3, bgcolor: 'background.paper', border: 1, borderColor: 'divider', mb: 3 }}>
        <Typography sx={{ ...legalBodyTextSx, fontWeight: 600, mb: 1 }}>
          Data Edge Services LLC
        </Typography>
        <Typography sx={{ ...legalBodyTextSx, mb: 1 }}>
          doing business as MyLojic
        </Typography>
        <Typography sx={{ ...legalBodyTextSx, mb: 2 }}>
          3000 Joe DiMaggio Blvd Ste 90, Round Rock, TX 78665
        </Typography>
        <Typography sx={legalBodyTextSx}>
          Email: legal@mylojic.com
        </Typography>
        <Typography sx={legalBodyTextSx}>
          Website: www.mylojic.com
        </Typography>
      </Paper>
    </LegalPageShell>
  );
};

export default Privacy;
